/**
 * The one way the SPA talks to `/api` (PRD §12).
 *
 * Every failure comes back as the `{ error: { code, … } }` envelope of `src/shared/errors.ts`,
 * so the UI can render `errors.<code>` through i18n instead of showing whatever text the server
 * happened to produce (CLAUDE.md hard rule 9).
 */

import i18n from "@/i18n";
import { formatWeekdayDate } from "@/lib/format";
import { formatCountdown, humanizeDuration, type DurationLocale } from "@/shared/duration";
import {
  isErrorCode,
  isRateLimitedPayload,
  type AnyErrorPayload,
  type ErrorCode,
} from "@/shared/errors";

export class ApiError extends Error {
  readonly status: number;
  readonly payload: AnyErrorPayload;

  constructor(status: number, payload: AnyErrorPayload) {
    super(payload.code);
    this.name = "ApiError";
    this.status = status;
    this.payload = payload;
  }

  get code(): ErrorCode {
    return this.payload.code;
  }

  /** The message to show a user: translated from the code, with the payload as interpolation. */
  get localizedMessage(): string {
    if (isRateLimitedPayload(this.payload)) {
      const payload = this.payload;
      const locale: DurationLocale = i18n.language.toLowerCase().startsWith("en") ? "en" : "de";
      const remaining =
        typeof payload.resetAt === "string"
          ? Math.max(0, Math.ceil((Date.parse(payload.resetAt) - Date.now()) / 1000))
          : payload.seconds;
      const key =
        payload.target === "login"
          ? "errors.RATE_LIMITED_LOGIN"
          : payload.target === "attachment_upload"
            ? "errors.RATE_LIMITED"
            : "errors.RATE_LIMITED_EMAIL";
      return i18n.t(key, {
        attempts: payload.attempts,
        window: humanizeDuration(payload.window_seconds, locale),
        countdown: formatCountdown(Number.isFinite(remaining) ? remaining : 0),
      });
    }
    if (
      this.payload.code === "CONFLICT" &&
      "reason" in this.payload &&
      this.payload.reason === "already_assigned"
    ) {
      return i18n.t("errors.ALREADY_ASSIGNED");
    }
    const date =
      this.payload.code === "CROSS_PROJECT_OVERBOOKING" && typeof this.payload.date === "string"
        ? this.payload.date
        : undefined;
    return i18n.t(`errors.${this.code}`, {
      defaultValue: i18n.t("errors.INTERNAL_ERROR"),
      ...this.payload,
      ...(date ? { date_label: formatWeekdayDate(date) } : {}),
    });
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  let payload: AnyErrorPayload = { code: "INTERNAL_ERROR" };
  try {
    const body: unknown = await response.json();
    const code = (body as { error?: { code?: unknown } })?.error?.code;
    if (isErrorCode(code)) payload = (body as { error: AnyErrorPayload }).error;
  } catch {
    // A non-JSON body (a proxy error page, say) stays an INTERNAL_ERROR.
  }
  return new ApiError(response.status, payload);
}

let onUnauthenticated: (() => void) | null = null;

/**
 * A cookie can die between two renders (expiry, a password reset elsewhere, a G-Admin disabling
 * the account). The first 401 tells the cache the session is gone so the guards send the person
 * to `/auth` instead of retrying against a dead session.
 */
export function setUnauthenticatedHandler(handle: (() => void) | null): void {
  onUnauthenticated = handle;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method,
    credentials: "include",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 401 && path !== "/auth/session") onUnauthenticated?.();
  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Binary uploads — optional filename query for week attachments. */
async function upload<T>(
  path: string,
  file: File | Blob,
  options?: { filename?: string },
): Promise<T> {
  const query =
    options?.filename !== undefined ? `?filename=${encodeURIComponent(options.filename)}` : "";
  const response = await fetch(`/api${path}${query}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/octet-stream" },
    body: await file.arrayBuffer(),
  });

  if (response.status === 401) onUnauthenticated?.();
  if (!response.ok) throw await toApiError(response);
  return (await response.json()) as T;
}

/** Binary downloads (Stundenzettel .xlsx) — returns the blob and a best-effort filename. */
async function download(path: string): Promise<{ blob: Blob; filename: string }> {
  const response = await fetch(`/api${path}`, { method: "GET", credentials: "include" });
  if (response.status === 401 && path !== "/auth/session") onUnauthenticated?.();
  if (!response.ok) throw await toApiError(response);

  const header = response.headers.get("Content-Disposition") ?? "";
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header)?.[1];
  const plain = /filename="([^"]+)"/i.exec(header)?.[1];
  const filename = decodeURIComponent(utf8 ?? plain ?? "download.bin");
  return { blob: await response.blob(), filename };
}

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body),
  upload,
  download,
};
